import React, { memo, useEffect, useRef, useState } from 'react';
import InputMask from 'react-input-mask';
import { InputTextProps } from './InputTextProps'
import { HtmlInputTypes, InputModes } from '../../../../models/form/IFieldsDefinition'
import clsx from 'clsx'


const InputText = memo((props: InputTextProps) => {
   const { htmlInputType, field, debounceTime, value, setHasInputFocused, handleOnPaste, handleOnKeyDown, handleOnBlur, handleSaveInputValueToStateAndValid } = props
   const defaultMaxLength = 50
   const currentTextInput = document.getElementsByName(field.name)[0] as HTMLInputElement; // Aktualna kontrolka, WAŻNE ABY NAZWA KONTROLKI BYŁA UNIKALNA
   const hasPageBeenRendered = useRef(false)
   const suggestionsRequestId = useRef(0)
   const [inputClear, setInputClear] = useState(true) //Hack używany do czyszczenia wartości kontrolki, w momencie zmiany typu dochodu na inny 
   const [currentInputValue, setCurrentInputValue] = useState<string>(value as string)
   const [suggestions, setSuggestions] = useState<string[]>([])
   const [isInputFocused, setIsInputFocused] = useState(false)
   const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1)
   const suggestionsListId = `${field.name}-suggestions-list`
   const normalizedInputValue = (currentInputValue ?? '').trim().toLowerCase()
   const exactMatchSuggestions = normalizedInputValue.length > 0
      ? suggestions.filter(suggestion => suggestion.trim().toLowerCase() === normalizedInputValue)
      : []
   const visibleSuggestions = exactMatchSuggestions.length > 0 ? exactMatchSuggestions : suggestions
   const shouldShowSuggestions = isInputFocused && visibleSuggestions.length > 0

   

   // =============================== USE EFFECT'S ===============================

   useEffect(() => { // Czyszczenie wartości z kontrolki przy zmianie na inny typ dochodu
      setCurrentInputValue(props.value)
      setInputClear(false)
   }, [props.value])

   useEffect(() => { //Debounce input
      const timeoutId = setTimeout(() => {
         if(hasPageBeenRendered.current && inputClear){ //Pierwsze błedy walidacyjne wyświetlamy dopiero gdy użytkownik zacznie uzupełniać kontrolki
            handleSaveInputValueToStateAndValid(currentInputValue)
            return
         }

         hasPageBeenRendered.current = true // Pierwszy render strony - nie chcemy od razu wyświetlać wszystkich błędów walidacyjnych
      }, debounceTime)

      return () => clearTimeout(timeoutId)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentInputValue, hasPageBeenRendered])

   useEffect(() => {
      const provider = field.suggestionsProvider
      if (!provider) {
         return
      }

      const trimmedValue = (currentInputValue ?? '').trim()
      const minLength = field.suggestionsMinLength ?? 2
      if (trimmedValue.length < minLength) {
         setSuggestions([])
         setHighlightedSuggestionIndex(-1)
         return
      }

      const requestId = ++suggestionsRequestId.current
      const timeoutId = setTimeout(async () => {
         try {
            const response = await provider(trimmedValue)
            if (requestId !== suggestionsRequestId.current) {
               return
            }

            const uniqueSuggestions = Array.from(new Set((response ?? []).filter(Boolean)))
            setSuggestions(uniqueSuggestions.slice(0, 10))
            setHighlightedSuggestionIndex(-1)
         } catch {
            if (requestId === suggestionsRequestId.current) {
               setSuggestions([])
               setHighlightedSuggestionIndex(-1)
            }
         }
      }, 250)

      return () => clearTimeout(timeoutId)
   }, [currentInputValue, field])

   useEffect(() => {
      if (highlightedSuggestionIndex < 0) {
         return
      }

      if (highlightedSuggestionIndex >= visibleSuggestions.length) {
         setHighlightedSuggestionIndex(-1)
      }
   }, [highlightedSuggestionIndex, visibleSuggestions.length])

    // ==========================================================================



    // =============================== HANDLERS ===============================

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {     
      field.parser !== undefined ? setCurrentInputValue(field.parser(e.target.value)) : setCurrentInputValue(e.target.value)
      setInputClear(true)
   }

   const handleOnClick = () => {
      if(htmlInputType === HtmlInputTypes.tel){ // Kontrolka z numerem telefonu ze względu na specyficzną maskę musi być obsłużona osobno
         if(Boolean(!currentInputValue.charAt(4).trim().length)){
            currentTextInput?.setSelectionRange(4, 4)
            return
         }
      }

      // Jeśli użytkownik NICZEGO NIE WPISAŁ, ustawiamy kursor na początku. UWAGA - MASKA MUSI ZACZYNAĆ SIĘ PUSTYM ZNAKIEM ABY FUNKCJA DZIAŁAŁA POPRAWNIE
      if(Boolean(!currentInputValue.charAt(0).trim().length)){ 
         currentTextInput?.setSelectionRange(0, 0)
         return
      }
   }

   const handleSelectSuggestion = (selectedValue: string) => {
      setCurrentInputValue(selectedValue)
      handleSaveInputValueToStateAndValid(selectedValue)
      setSuggestions([])
      setHighlightedSuggestionIndex(-1)
   }

   const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (shouldShowSuggestions) {
         if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightedSuggestionIndex((prev) => {
               if (prev < 0) {
                  return 0
               }

               return prev >= visibleSuggestions.length - 1 ? 0 : prev + 1
            })
            return
         }

         if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightedSuggestionIndex((prev) => {
               if (prev < 0) {
                  return visibleSuggestions.length - 1
               }

               return prev <= 0 ? visibleSuggestions.length - 1 : prev - 1
            })
            return
         }

         if (e.key === 'Enter' && highlightedSuggestionIndex >= 0 && highlightedSuggestionIndex < visibleSuggestions.length) {
            e.preventDefault()
            handleSelectSuggestion(visibleSuggestions[highlightedSuggestionIndex])
            return
         }

         if (e.key === 'Escape') {
            e.preventDefault()
            setSuggestions([])
            setHighlightedSuggestionIndex(-1)
            return
         }
      }

      handleOnKeyDown && handleOnKeyDown(e)
   }
   // ==========================================================================

   return (
      <>
      <InputMask
         id={field.name}
         name={field.name}
         value={currentInputValue}
         mask={field.mask || ''}
         maskChar={''}
         disabled={!field.editable}
         inputMode={field.inputMode ? field.inputMode : InputModes.text}
         maxLength={!field.mask ? ( field.maxLength ?? defaultMaxLength ) : undefined} //Jeśli jest maska ona określa ile znaków można wpisać w kontrolkę. Jeśli nie ma maski i nie ma "maxLength" maksymalnie można wpisać 50 znaków
         type={htmlInputType}
         role='combobox'
         aria-autocomplete='list'
         aria-expanded={shouldShowSuggestions}
         aria-controls={shouldShowSuggestions ? suggestionsListId : undefined}
         aria-activedescendant={shouldShowSuggestions && highlightedSuggestionIndex >= 0 ? `${field.name}-suggestion-${highlightedSuggestionIndex}` : undefined}
         onClick={handleOnClick}
         onFocus={() => {
            setHasInputFocused(true)
            setIsInputFocused(true)
         }}
         onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange(e)}
         onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => handleOnPaste && handleOnPaste(e)}
         onKeyDown={handleInputKeyDown}
         onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            setHasInputFocused(false)
            setIsInputFocused(false)
            setHighlightedSuggestionIndex(-1)
            handleOnBlur(e)
         }}
         {...props.others}
      />
      {shouldShowSuggestions && (
         <div className='input-text-suggestions-container' id={suggestionsListId} role='listbox' aria-label={`Sugestie dla ${field.label}`}>
            {visibleSuggestions.map((suggestion, index) => (
               <button
                  id={`${field.name}-suggestion-${index}`}
                  key={suggestion}
                  type='button'
                  role='option'
                  aria-selected={highlightedSuggestionIndex === index}
                  className={clsx('input-text-suggestion-item', highlightedSuggestionIndex === index && 'input-text-suggestion-item-active')}
                  onMouseDown={(e) => {
                     e.preventDefault()
                     handleSelectSuggestion(suggestion)
                  }}
                  onMouseEnter={() => setHighlightedSuggestionIndex(index)}
               >
                  {suggestion}
               </button>
            ))}
         </div>
      )}
   </>
   )
})

export default InputText